/**
 * Tag Editor Component - Left Panel
 * Displays and edits the currently focused tag candidate
 */

import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { TagCandidate } from '../../types';
import { useState, useEffect } from 'react';
import ConfidenceScore from '../common/ConfidenceScore';
import LimiterGroupAccordion, { LimiterGroupWithIndex } from './LimiterGroupAccordion';
import MathtasticLimiterGroupAccordion from './MathtasticLimiterGroupAccordion';
import { isLimiterGroupMathtastic } from '../../services/spine.service';

interface TagEditorProps {
  tag: TagCandidate | null;
  tagIndex: number | null;
  lessonId: string;
  onUpdate: (updatedTag: TagCandidate, tagIndex: number) => void;
}


export default function TagEditor({ tag, tagIndex, lessonId, onUpdate }: TagEditorProps) {
  const [localTag, setLocalTag] = useState<TagCandidate | null>(tag);

  // Update local state when the tag prop changes from outside (e.g., switching to a different tag)
  useEffect(() => {
    setLocalTag(tag);
  }, [tag?.competency?.spine_node_id]); // Only react to actual tag switches, not object reference changes

  if (!tag || tagIndex === null) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body2" color="textSecondary">
          Select a tag from the chart or tag list to edit
        </Typography>
      </Box>
    );
  }

  const competency = localTag?.competency || tag.competency;
  // Helper function to sort items by confidence score (desc) then description (asc) while preserving original indices
  const sortByConfidenceAndDescription = <T extends { confidence_score: number; description: string }>(items: T[]) => {
    return items
      .map((item, originalIndex) => ({ item, originalIndex }))
      .sort((a, b) => {
        // First sort by confidence score (descending - highest first)
        if (a.item.confidence_score !== b.item.confidence_score) {
          return b.item.confidence_score - a.item.confidence_score;
        }
        // Then sort by description (ascending - alphabetical)
        return a.item.description.localeCompare(b.item.description);
      });
  };

  const rawLimiterGroups = localTag?.limiter_groups || tag.limiter_groups;

  // Helper function to clean limiter description by removing group name
  const cleanLimiterDescription = (limiterDescription: string, groupDescription: string): string => {
    // Check if group name exists in limiter description (case-insensitive)
    const limiterLower = limiterDescription.toLowerCase();
    const groupLower = groupDescription.toLowerCase();

    if (limiterLower.includes(groupLower)) {
      // Find the position and remove the group name (preserving original case)
      const index = limiterLower.indexOf(groupLower);
      let cleaned = limiterDescription.substring(0, index) + limiterDescription.substring(index + groupDescription.length);

      // Remove leading spaces and "/" until we reach first alphanumeric character
      cleaned = cleaned.replace(/^[\s/]+/, '');

      // Return cleaned version, or original if nothing meaningful left
      return cleaned || limiterDescription;
    }

    return limiterDescription;
  };

  // Get sorted limiter groups with sorted limiters within each group, preserving original indices
  const limiterGroups = rawLimiterGroups ? sortByConfidenceAndDescription(
    rawLimiterGroups.map(group => ({
      ...group,
      limiters: sortByConfidenceAndDescription(group.limiters).map(({ item, originalIndex }) => ({
        ...item,
        originalLimiterIndex: originalIndex
      }))
    }))
  ).map(({ item, originalIndex }) => ({
    ...item,
    originalGroupIndex: originalIndex,
    limiters: item.limiters
  })) : [];

  // Handle competency selection change
  const handleCompetencyChange = (selected: boolean) => {
    const updatedTag = { ...(localTag || tag) };
    updatedTag.competency = { ...updatedTag.competency, selected };
    setLocalTag(updatedTag);
    onUpdate(updatedTag, tagIndex);
  };

  // Handle limiter group selection change
  const handleLimiterGroupChange = (groupIndex: number, selected: boolean) => {
    const updatedTag = { ...(localTag || tag) };
    updatedTag.limiter_groups = [...(updatedTag.limiter_groups || [])];
    updatedTag.limiter_groups[groupIndex] = {
      ...updatedTag.limiter_groups[groupIndex],
      selected,
    };

    // If deselecting group, deselect all child limiters and clear math expression selections
    if (!selected) {
      updatedTag.limiter_groups[groupIndex].limiters = updatedTag.limiter_groups[
        groupIndex
      ].limiters.map((limiter) => ({
        ...limiter,
        selected: false,
      }));
      // Clear math expression selections for mathtastic groups
      updatedTag.limiter_groups[groupIndex].selected_math_expression_ids = [];
    }

    setLocalTag(updatedTag);
    onUpdate(updatedTag, tagIndex);
  };

  // Handle individual limiter selection change
  const handleLimiterChange = (groupIndex: number, limiterIndex: number, selected: boolean) => {
    const updatedTag = { ...(localTag || tag) };
    updatedTag.limiter_groups = [...(updatedTag.limiter_groups || [])];
    updatedTag.limiter_groups[groupIndex] = {
      ...updatedTag.limiter_groups[groupIndex],
      limiters: [...updatedTag.limiter_groups[groupIndex].limiters],
    };
    updatedTag.limiter_groups[groupIndex].limiters[limiterIndex] = {
      ...updatedTag.limiter_groups[groupIndex].limiters[limiterIndex],
      selected,
    };

    // If selecting a limiter and the group is not selected, automatically select the group
    if (selected && !updatedTag.limiter_groups[groupIndex].selected) {
      updatedTag.limiter_groups[groupIndex] = {
        ...updatedTag.limiter_groups[groupIndex],
        selected: true,
      };
    }

    setLocalTag(updatedTag);
    onUpdate(updatedTag, tagIndex);
  };

  // Handle math expression selection change for mathtastic limiter groups
  // Also handles auto-selecting the group when an expression is selected
  const handleMathExpressionSelectionChange = (groupIndex: number, selectedIds: string[], autoSelectGroup?: boolean) => {
    const updatedTag = { ...(localTag || tag) };
    updatedTag.limiter_groups = [...(updatedTag.limiter_groups || [])];
    updatedTag.limiter_groups[groupIndex] = {
      ...updatedTag.limiter_groups[groupIndex],
      selected_math_expression_ids: selectedIds,
    };

    // Auto-select the group if requested and not already selected
    if (autoSelectGroup && !updatedTag.limiter_groups[groupIndex].selected) {
      updatedTag.limiter_groups[groupIndex].selected = true;
    }

    setLocalTag(updatedTag);
    onUpdate(updatedTag, tagIndex);
  };


  return (
    <Box>
      {/* Competency Section */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Competency
        </Typography>
        <Box
          sx={{
            p: 2,
            bgcolor: competency.selected ? 'rgba(99, 102, 241, 0.15)' : 'background.default',
            borderRadius: 1,
            border: 1,
            borderColor: competency.selected ? 'primary.main' : 'divider',
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={competency.selected}
                onChange={(e) => handleCompetencyChange(e.target.checked)}
              />
            }
            label={
              <Box sx={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{competency.description}</Typography>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                  ID: {competency.spine_node_id}
                </Typography>
              </Box>
            }
          />
          <Box sx={{ mt: 1, ml: 4 }}>
            <ConfidenceScore score={competency.confidence_score} reasoning={competency.reasoning} />
          </Box>
        </Box>
      </Box>

      {/* Limiter Groups Section */}
      {limiterGroups && limiterGroups.length > 0 && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Limiter Groups
          </Typography>
          {limiterGroups.map((group) => {
            // Check if this is a mathtastic (P-type) limiter group
            const isMathtastic = isLimiterGroupMathtastic(group as LimiterGroupWithIndex);

            return isMathtastic ? (
              <MathtasticLimiterGroupAccordion
                key={group.limiter_group_id}
                group={group}
                lessonId={lessonId}
                onGroupChange={handleLimiterGroupChange}
                onLimiterChange={handleLimiterChange}
                onMathExpressionSelectionChange={handleMathExpressionSelectionChange}
                cleanLimiterDescription={cleanLimiterDescription}
              />
            ) : (
              <LimiterGroupAccordion
                key={group.limiter_group_id}
                group={group}
                onGroupChange={handleLimiterGroupChange}
                onLimiterChange={handleLimiterChange}
                cleanLimiterDescription={cleanLimiterDescription}
              />
            );
          })}
        </Box>
      )}
    </Box>
  );
}
